/* sb-dc-tplgen.js — draws the built-in design templates.

   dc_templates rows whose file_url starts with "dcgen:" carry a tiny
   base64 JSON spec instead of a stored file. Both the Design Templates
   page (thumbnails) and the design center bridge (canvas injection)
   call window.__dcTplGen(spec, w, h) to turn that spec into SVG.

   Why a generator and not stored files: one edit here restyles every
   built-in template, and the rows stay a few hundred bytes each.

   Only system fonts are used. An SVG loaded through <img> or as a Konva
   image is an isolated document — it cannot reach the page's webfonts,
   so anything else would silently fall back to Times.
*/
(function () {
  'use strict';

  var PAL = {
    navy:   { bg: '#0d2145', ink: '#ffffff', acc: '#e8b44a', acc2: '#c8102e' },
    red:    { bg: '#b3141f', ink: '#ffffff', acc: '#ffd34d', acc2: '#7a0d15' },
    forest: { bg: '#14402c', ink: '#f6f2e6', acc: '#d8a72e', acc2: '#0a2418' },
    ink:    { bg: '#141416', ink: '#ffffff', acc: '#ff7a1a', acc2: '#3a3a40' },
    teal:   { bg: '#0b5c66', ink: '#ffffff', acc: '#ffca3a', acc2: '#073f46' },
    maroon: { bg: '#5c1030', ink: '#f7eef2', acc: '#e0b563', acc2: '#3d0a20' },
    royal:  { bg: '#1b2f8a', ink: '#ffffff', acc: '#ffd400', acc2: '#101d59' },
    cream:  { bg: '#f4efe2', ink: '#23201a', acc: '#b3141f', acc2: '#c9bfa4' },
    slate:  { bg: '#2b3440', ink: '#ffffff', acc: '#5ec2b7', acc2: '#1b2129' },
    plum:   { bg: '#3d1f5c', ink: '#ffffff', acc: '#f0c419', acc2: '#26123a' },
    sky:    { bg: '#0f6fb5', ink: '#ffffff', acc: '#ffffff', acc2: '#0a4f82' },
    sand:   { bg: '#c8781e', ink: '#ffffff', acc: '#2b1a0c', acc2: '#9c5a12' }
  };

  var BOLD = "Impact, Haettenschweiler, 'Arial Narrow Bold', 'Arial Black', sans-serif";
  var SANS = "'Trebuchet MS', 'Segoe UI', Arial, sans-serif";
  var SERIF = "Georgia, 'Times New Roman', serif";

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* Rough advance width per character, as a fraction of font size.
     Impact is narrow, Georgia is wide — good enough to keep text inside
     the board without measuring. */
  function widthFactor(font) {
    if (font === BOLD) return 0.52;
    if (font === SERIF) return 0.55;
    return 0.54;
  }

  /* Largest size that fits `text` in `boxW`, capped at `max`. */
  function fitFont(t, boxW, max, font) {
    var n = Math.max(1, String(t || '').length);
    return Math.max(8, Math.min(max, boxW / (n * widthFactor(font))));
  }

  function text(t, x, y, size, font, fill, opts) {
    opts = opts || {};
    var a = ['x="' + r(x) + '"', 'y="' + r(y) + '"',
      'font-family="' + font + '"', 'font-size="' + r(size) + '"',
      'fill="' + fill + '"', 'text-anchor="' + (opts.anchor || 'middle') + '"',
      'dominant-baseline="central"'];
    if (opts.spacing) a.push('letter-spacing="' + r(opts.spacing) + '"');
    if (opts.weight) a.push('font-weight="' + opts.weight + '"');
    return '<text ' + a.join(' ') + '>' + esc(t) + '</text>';
  }
  function rect(x, y, w, h, fill, extra) {
    return '<rect x="' + r(x) + '" y="' + r(y) + '" width="' + r(w) + '" height="' + r(h) +
      '" fill="' + fill + '"' + (extra || '') + '/>';
  }
  function r(n) { return Math.round(n * 100) / 100; }

  /* ---------------------------------------------------------- layouts */
  /* Every layout receives the board size, so a template typesets itself
     at whatever aspect ratio the customer picked. */
  var LAYOUT = {

    stack: function (W, H, c, t) {
      var o = rect(0, 0, W, H, c.bg);
      var f1 = fitFont(t[0], W * 0.84, H * 0.34, BOLD);
      o += text(t[0], W / 2, H * 0.38, f1, BOLD, c.acc, { spacing: f1 * 0.02 });
      o += rect(W * 0.28, H * 0.55, W * 0.44, Math.max(2, H * 0.012), c.acc2);
      if (t[1]) {
        var f2 = fitFont(t[1], W * 0.7, H * 0.13, SANS);
        o += text(t[1], W / 2, H * 0.67, f2, SANS, c.ink, { weight: 'bold', spacing: f2 * 0.06 });
      }
      if (t[2]) o += text(t[2], W / 2, H * 0.82, fitFont(t[2], W * 0.6, H * 0.085, SANS), SANS, c.ink);
      return o;
    },

    band: function (W, H, c, t) {
      var b = H * 0.11;
      var o = rect(0, 0, W, H, c.bg) + rect(0, 0, W, b, c.acc) + rect(0, H - b, W, b, c.acc);
      var f1 = fitFont(t[0], W * 0.86, H * 0.36, BOLD);
      o += text(t[0], W / 2, H * 0.42, f1, BOLD, c.ink);
      if (t[1]) o += text(t[1], W / 2, H * 0.63, fitFont(t[1], W * 0.72, H * 0.14, SANS), SANS, c.acc, { weight: 'bold' });
      if (t[2]) o += text(t[2], W / 2, H * 0.78, fitFont(t[2], W * 0.6, H * 0.09, SANS), SANS, c.ink);
      return o;
    },

    block: function (W, H, c, t) {
      var tall = H > W;
      var o = rect(0, 0, W, H, c.bg);
      if (tall) {
        o += rect(0, 0, W, H * 0.42, c.acc2);
        o += text(t[0], W / 2, H * 0.21, fitFont(t[0], W * 0.82, H * 0.2, BOLD), BOLD, c.acc);
        if (t[1]) o += text(t[1], W / 2, H * 0.58, fitFont(t[1], W * 0.8, H * 0.13, SANS), SANS, c.ink, { weight: 'bold' });
        if (t[2]) o += text(t[2], W / 2, H * 0.76, fitFont(t[2], W * 0.7, H * 0.09, SANS), SANS, c.ink);
      } else {
        o += rect(0, 0, W * 0.38, H, c.acc2);
        o += text(t[0], W * 0.19, H * 0.5, fitFont(t[0], W * 0.32, H * 0.3, BOLD), BOLD, c.acc);
        if (t[1]) o += text(t[1], W * 0.68, H * 0.42, fitFont(t[1], W * 0.5, H * 0.19, SANS), SANS, c.ink, { weight: 'bold' });
        if (t[2]) o += text(t[2], W * 0.68, H * 0.63, fitFont(t[2], W * 0.5, H * 0.11, SANS), SANS, c.ink);
      }
      return o;
    },

    frame: function (W, H, c, t) {
      var m = Math.min(W, H) * 0.07, sw = Math.max(2, Math.min(W, H) * 0.012);
      var o = rect(0, 0, W, H, c.bg);
      o += '<rect x="' + r(m) + '" y="' + r(m) + '" width="' + r(W - 2 * m) + '" height="' + r(H - 2 * m) +
        '" fill="none" stroke="' + c.acc + '" stroke-width="' + r(sw) + '"/>';
      var f1 = fitFont(t[0], W * 0.7, H * 0.26, SERIF);
      o += text(t[0], W / 2, H * 0.42, f1, SERIF, c.ink, { weight: 'bold' });
      if (t[1]) o += text(t[1], W / 2, H * 0.6, fitFont(t[1], W * 0.62, H * 0.12, SERIF), SERIF, c.acc, { spacing: 1.5 });
      if (t[2]) o += text(t[2], W / 2, H * 0.74, fitFont(t[2], W * 0.55, H * 0.085, SANS), SANS, c.ink);
      return o;
    },

    badge: function (W, H, c, t) {
      var cx = W / 2, cy = H * 0.44, rx = Math.min(W * 0.42, H * 0.62), ry = Math.min(H * 0.34, rx);
      var o = rect(0, 0, W, H, c.bg);
      o += '<ellipse cx="' + r(cx) + '" cy="' + r(cy) + '" rx="' + r(rx) + '" ry="' + r(ry) +
        '" fill="none" stroke="' + c.acc + '" stroke-width="' + r(Math.min(W, H) * 0.018) + '"/>';
      o += text(t[0], cx, cy - ry * 0.18, fitFont(t[0], rx * 1.5, ry * 0.75, BOLD), BOLD, c.acc);
      if (t[1]) o += text(t[1], cx, cy + ry * 0.42, fitFont(t[1], rx * 1.4, ry * 0.3, SANS), SANS, c.ink, { weight: 'bold', spacing: 1.2 });
      if (t[2]) o += text(t[2], cx, H * 0.87, fitFont(t[2], W * 0.7, H * 0.1, SANS), SANS, c.ink);
      return o;
    },

    split: function (W, H, c, t) {
      var o = rect(0, 0, W, H, c.bg) + rect(0, H * 0.5, W, H * 0.5, c.acc2);
      var f1 = fitFont(t[0], W * 0.88, H * 0.3, BOLD);
      o += text(t[0], W / 2, H * 0.3, f1, BOLD, c.acc);
      o += rect(W * 0.1, H * 0.495, W * 0.8, Math.max(2, H * 0.01), c.acc);
      if (t[1]) o += text(t[1], W / 2, H * 0.68, fitFont(t[1], W * 0.8, H * 0.17, SANS), SANS, c.ink, { weight: 'bold' });
      if (t[2]) o += text(t[2], W / 2, H * 0.85, fitFont(t[2], W * 0.66, H * 0.09, SANS), SANS, c.ink);
      return o;
    },

    corner: function (W, H, c, t) {
      var k = Math.min(W, H) * 0.5;
      var o = rect(0, 0, W, H, c.bg);
      o += '<polygon points="0,0 ' + r(k) + ',0 0,' + r(k) + '" fill="' + c.acc + '"/>';
      o += '<polygon points="' + r(W) + ',' + r(H) + ' ' + r(W - k) + ',' + r(H) + ' ' + r(W) + ',' + r(H - k) + '" fill="' + c.acc + '"/>';
      var f1 = fitFont(t[0], W * 0.7, H * 0.32, BOLD);
      o += text(t[0], W / 2, H * 0.44, f1, BOLD, c.ink);
      if (t[1]) o += text(t[1], W / 2, H * 0.64, fitFont(t[1], W * 0.6, H * 0.16, SANS), SANS, c.acc, { weight: 'bold' });
      if (t[2]) o += text(t[2], W / 2, H * 0.78, fitFont(t[2], W * 0.55, H * 0.09, SANS), SANS, c.ink);
      return o;
    }
  };

  /* ------------------------------------------------------------- api */
  function decode(fileUrl) {
    try {
      var b64 = String(fileUrl).slice(6);            // strip "dcgen:"
      var s = atob(b64.replace(/-/g, '+').replace(/_/g, '/'));
      var bytes = new Uint8Array(s.length);
      for (var i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch (e) { return null; }
  }

  window.__dcIsGen = function (fileUrl) {
    return typeof fileUrl === 'string' && fileUrl.indexOf('dcgen:') === 0;
  };

  window.__dcTplGen = function (specOrUrl, w, h) {
    var spec = (typeof specOrUrl === 'string') ? decode(specOrUrl) : specOrUrl;
    if (!spec) return null;
    var fit = spec.f || 'wide';
    if (!(w > 0 && h > 0)) {
      w = fit === 'tall' ? 600 : 1200;
      h = fit === 'tall' ? 1200 : (fit === 'square' ? 1200 : 600);
    }
    var c = PAL[spec.p] || PAL.navy;
    var draw = LAYOUT[spec.l] || LAYOUT.stack;
    var body;
    try { body = draw(w, h, c, spec.t || []); }
    catch (e) { body = rect(0, 0, w, h, c.bg); }
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + r(w) + ' ' + r(h) +
      '" width="' + r(w) + '" height="' + r(h) + '">' + body + '</svg>';
  };

  window.__dcTplGenUrl = function (specOrUrl, w, h) {
    var svg = window.__dcTplGen(specOrUrl, w, h);
    if (!svg) return null;
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  };
})();
